/**
 * Automatic access by email domain (docs/PERMISSIONS.md → "Automatic access").
 *
 * An Admin lists the domains whose people may join without waiting — each with
 * the role they join as. Someone who CONFIRMS a sign-up with an address at a
 * listed domain joins straight away with that role; everyone else still waits
 * as Requested. Pure rules, so the server, Test mode and the tests all match
 * the same way.
 *
 *  - Exact match on everything after the last `@`, trimmed and lower-cased.
 *    `interautomate.com` never matches `evil-interautomate.com`,
 *    `interautomate.com.evil.io` or `mail.interautomate.com` — a subdomain has
 *    to be listed itself.
 *  - Public mail domains can't be listed: anyone on earth can have a Gmail
 *    address, so "everyone at gmail.com" means "everyone".
 *  - Only ever applied when an address is confirmed, never to an unverified
 *    one, and an invitation's role always wins (src/adapters/auth/membership.ts).
 */
import { z } from 'zod'
import { type AssignableRole, assignableRoles } from '../access/index.ts'

/**
 * Free, public mail providers. Short and explicit on purpose: it exists to
 * catch the obvious mistake, not to be a directory of every provider. Add to
 * it when a real one is missed.
 */
export const PUBLIC_MAIL_DOMAINS = [
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'yahoo.com',
  'ymail.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'pm.me',
  'gmx.com',
  'mail.com',
  'zoho.com',
  'yandex.com',
  'fastmail.com',
] as const

const publicDomains = new Set<string>(PUBLIC_MAIL_DOMAINS)

/**
 * One label: letters, digits and hyphens, not starting or ending with a
 * hyphen, at most 63 characters. The last label (the TLD) must be letters, or
 * an `xn--` internationalised one.
 */
const LABEL = /^(?!-)[a-z0-9-]{1,63}(?<!-)$/
const TLD = /^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/

/** `  @InterAutomate.COM ` → `interautomate.com`. Does not validate. */
export function normalizeDomain(input: string): string {
  return input.trim().toLowerCase().replace(/^@+/, '').replace(/\.$/, '')
}

/** Why a domain can't be listed — or null when it can. Expects a normalised domain. */
export function domainProblem(domain: string): string | null {
  if (!domain) return 'Enter a domain, like example.com.'
  if (domain.includes('@')) return 'Enter just the part after the @, like example.com.'
  if (domain.includes('*')) return 'Wildcards aren’t allowed. List each domain exactly.'
  if (domain.length > 253) return 'That domain is too long.'
  const labels = domain.split('.')
  const tld = labels.at(-1) ?? ''
  if (labels.length < 2 || !labels.every((label) => LABEL.test(label)) || !TLD.test(tld)) {
    return 'That isn’t a valid domain. Use something like example.com.'
  }
  if (publicDomains.has(domain)) {
    return `${domain} is a public email provider — anyone can get an address there, so it can’t give automatic access.`
  }
  return null
}

/** A domain as an Admin types it: normalised, then checked. */
export const autoJoinDomainSchema = z
  .string()
  .max(300, 'That domain is too long.')
  .transform(normalizeDomain)
  .superRefine((domain, ctx) => {
    const problem = domainProblem(domain)
    if (problem) ctx.addIssue({ code: 'custom', message: problem })
  })

/** Adding a rule: the domain and the role its people join as. */
export const autoJoinRuleInputSchema = z.object({
  domain: autoJoinDomainSchema,
  role: z.enum(assignableRoles),
})
export type AutoJoinRuleInput = z.infer<typeof autoJoinRuleInputSchema>

/** A rule as stored and listed. */
export const autoJoinRuleSchema = z.object({
  id: z.string().min(1),
  domain: z.string().min(1),
  role: z.enum(assignableRoles),
  createdAt: z.iso.datetime(),
  /** Who added it — a person's name, or the deploy setting that seeded it. */
  createdByName: z.string(),
})
export type AutoJoinRule = z.infer<typeof autoJoinRuleSchema>

/**
 * The domain of an email address: everything after the LAST `@`, trimmed and
 * lower-cased, without a trailing dot — the same tidying `normalizeDomain`
 * gives a listed domain, so `jo@example.com.` and `example.com` agree. Null when
 * there is no `@` or nothing after it.
 */
export function domainOfEmail(email: string): string | null {
  const trimmed = email.trim().toLowerCase()
  const at = trimmed.lastIndexOf('@')
  if (at < 1) return null
  const domain = trimmed.slice(at + 1).replace(/\.$/, '')
  return domain || null
}

/** The rule this address falls under — an exact domain match — or null. */
export function matchAutoJoinRule<T extends { domain: string }>(
  email: string,
  rules: readonly T[],
): T | null {
  const domain = domainOfEmail(email)
  if (!domain) return null
  return rules.find((rule) => normalizeDomain(rule.domain) === domain) ?? null
}

/**
 * The role someone joins with when their sign-up is confirmed, and why. First
 * match wins:
 *
 *  1. the first account ever founds the organization and is its Admin;
 *  2. an invitation's role — an Admin chose it for this very person, which is
 *     more specific than a rule for everyone at their domain;
 *  3. a domain rule, only when the address was just confirmed;
 *  4. otherwise Requested: the waiting room.
 */
export type JoinReason = 'founded' | 'invited' | 'domain' | 'requested'

export function joinRoleFor(facts: {
  founding: boolean
  invitedRole: AssignableRole | null
  domainRule: { domain: string; role: AssignableRole } | null
  /** True only at the moment an address is confirmed. */
  confirmed: boolean
}): { role: AssignableRole | 'requested'; reason: JoinReason; domain?: string } {
  if (facts.founding) return { role: 'admin', reason: 'founded' }
  if (facts.invitedRole) return { role: facts.invitedRole, reason: 'invited' }
  if (facts.confirmed && facts.domainRule) {
    return { role: facts.domainRule.role, reason: 'domain', domain: facts.domainRule.domain }
  }
  return { role: 'requested', reason: 'requested' }
}

/**
 * Who a rule set at deploy is credited to. The activity log keeps this
 * technical name — it says exactly where the rule came from; the Users page
 * says it in words (`addedByLine`).
 */
export const DEPLOY_SETTING_NAME = 'AUTO_JOIN_DOMAINS (set at deploy)'

/** "Added by Ava Chen" — or, for a rule set at deploy, "Added when the app was set up". */
export function addedByLine(rule: Pick<AutoJoinRule, 'createdByName'>): string {
  return rule.createdByName === DEPLOY_SETTING_NAME
    ? 'Added when the app was set up'
    : `Added by ${rule.createdByName}`
}

/** One entry of `AUTO_JOIN_DOMAINS` that was left out, and why, in plain words. */
export interface IgnoredAutoJoinEntry {
  entry: string
  reason: string
}

/**
 * The deploy setting `AUTO_JOIN_DOMAINS`, e.g. `"interautomate.com:admin,
 * example.co:staff"`. A domain without a role joins as Staff.
 *
 * Read entry by entry, and never fails: a typo must not take the whole app
 * down. Each entry is checked like one typed in Settings; a bad one — not a
 * domain, a public mail provider, an unknown role, listed twice — is left out
 * and reported, and the good ones still count. The server logs what was left
 * out, and the setup checklist (System → Fix them, /setup) shows it as a
 * warning (src/adapters/setup/probes.ts).
 */
export function parseAutoJoinDomains(value: string | undefined): {
  rules: AutoJoinRuleInput[]
  ignored: IgnoredAutoJoinEntry[]
} {
  const rules: AutoJoinRuleInput[] = []
  const ignored: IgnoredAutoJoinEntry[] = []
  const entries = (value ?? '')
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
  for (const entry of entries) {
    const [domainPart = '', rolePart = 'staff', ...extra] = entry.split(':')
    const role = rolePart.trim().toLowerCase()
    const domain = normalizeDomain(domainPart)
    // Each reason reads on from the entry: "‘gmail.com’ is a public email provider".
    const reason =
      extra.length > 0
        ? 'should be written domain:role'
        : publicDomains.has(domain)
          ? 'is a public email provider'
          : domainProblem(domain)
            ? 'isn’t a domain'
            : role !== 'admin' && role !== 'staff'
              ? 'needs the role admin or staff'
              : rules.some((rule) => rule.domain === domain)
                ? 'is listed twice'
                : null
    if (reason) ignored.push({ entry, reason })
    else rules.push({ domain, role: role as AssignableRole })
  }
  return { rules, ignored }
}

/** "1 entry ignored: ‘interautomate’ isn’t a domain." — for the log and the checklist. */
export function describeIgnoredEntries(ignored: readonly IgnoredAutoJoinEntry[]): string {
  const each = ignored.map((i) => `‘${i.entry}’ ${i.reason}`).join('; ')
  return `${ignored.length} ${ignored.length === 1 ? 'entry' : 'entries'} ignored: ${each}.`
}
